import { useState, useEffect } from "react";
import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Sparkles,
  Loader2,
  FolderOpen,
  Check,
  X,
  AlertCircle,
  Share2,
  Copy,
  ExternalLink,
  FileText,
  Calendar,
  Eye,
  GripVertical,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tables } from "@/integrations/supabase/types";
import { DocumentPreviewSheet } from "./DocumentPreviewSheet";

type Document = Tables<"documents">;

interface SearchResult {
  document: Document;
  found: boolean;
  reason?: string;
}

interface DocumentCollectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_LABELS: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  tax: "Document fiscal",
  insurance: "Assurance",
  receipt: "Reçu",
  quote: "Devis",
  identity: "Identité",
  payslip: "Bulletin de salaire",
  attestation: "Attestation",
  bank_statement: "Relevé bancaire",
  medical_certificate: "Certificat médical",
  notarial_deed: "Acte notarié",
  administrative: "Administratif",
  warranty: "Garantie",
  other: "Document",
};

const TYPE_EMOJIS: Record<string, string> = {
  invoice: "🧾",
  contract: "📝",
  tax: "📊",
  insurance: "🛡️",
  receipt: "🧾",
  quote: "💰",
  identity: "🪪",
  payslip: "💵",
  attestation: "📋",
  bank_statement: "🏦",
  medical_certificate: "🏥",
  notarial_deed: "⚖️",
  administrative: "📁",
  warranty: "🔧",
  other: "📄",
};

const EXAMPLE_QUERIES = [
  "3 derniers bulletins de salaire",
  "Avis d'imposition 2024",
  "Taxe foncière",
  "Justificatif de domicile",
  "Carte d'identité",
  "RIB",
  "Factures EDF",
  "KBIS",
  "Attestation d'assurance",
];

// Draggable item component with drag handle
const DraggableDocumentItem = ({
  result,
  isSelected,
  onToggle,
  onPreview,
}: {
  result: SearchResult;
  isSelected: boolean;
  onToggle: () => void;
  onPreview: () => void;
}) => {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={result}
      dragListener={false}
      dragControls={dragControls}
      className="list-none"
    >
      <Card
        className={cn(
          "p-3 transition-all",
          isSelected
            ? "border-primary bg-primary/5"
            : "hover:border-border"
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className="cursor-grab active:cursor-grabbing touch-none p-1 -m-1 hover:bg-muted rounded"
            onPointerDown={(e) => dragControls.start(e)}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground/50 shrink-0" />
          </div>
          
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggle}
          />
          
          <div 
            className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0 cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={onPreview}
            title="Voir l'aperçu"
          >
            <span className="text-lg">
              {TYPE_EMOJIS[result.document.doc_type] || "📄"}
            </span>
          </div>

          <div 
            className="flex-1 min-w-0 cursor-pointer"
            onClick={onToggle}
          >
            <p className="font-medium text-sm line-clamp-1">
              {result.document.title}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="secondary" className="text-[10px]">
                {TYPE_LABELS[result.document.doc_type] || "Document"}
              </Badge>
              {result.document.doc_date && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(result.document.doc_date).toLocaleDateString("fr-FR")}
                </span>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            title="Aperçu"
          >
            <Eye className="w-4 h-4 text-muted-foreground" />
          </Button>

          {result.found ? (
            <Check className="w-5 h-5 text-green-500 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
          )}
        </div>
      </Card>
    </Reorder.Item>
  );
};

const DocumentCollector = ({ open, onOpenChange }: DocumentCollectorProps) => {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [orderedResults, setOrderedResults] = useState<SearchResult[]>([]);
  const [collectionName, setCollectionName] = useState("");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [step, setStep] = useState<"search" | "select" | "share">("search");
  const [selectedExamples, setSelectedExamples] = useState<Set<string>>(new Set());
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setQuery("");
      setResults([]);
      setSelectedDocs(new Set());
      setOrderedResults([]);
      setCollectionName("");
      setShareUrl(null);
      setStep("search");
      setSelectedExamples(new Set());
      setPreviewDoc(null);
    }
  }, [open]);

  // Sync orderedResults when results change
  useEffect(() => {
    setOrderedResults(results);
  }, [results]);

  // Sync query with selected examples
  useEffect(() => {
    if (selectedExamples.size > 0) {
      const examplesArray = Array.from(selectedExamples);
      setQuery(examplesArray.join("\n"));
    }
  }, [selectedExamples]);

  const toggleExample = (example: string) => {
    const newSelected = new Set(selectedExamples);
    if (newSelected.has(example)) {
      newSelected.delete(example);
    } else {
      newSelected.add(example);
    }
    setSelectedExamples(newSelected);
  };

  const handleSearch = async () => {
    if (!query.trim()) return;

    setSearching(true);
    setResults([]);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Split by newlines, commas, semicolons, "et", "+"
      const parts = query
        .split(/[\n,;]|\bet\b|\b\+\b/i)
        .map(p => p.trim())
        .filter(p => p.length > 2);
      
      const searchQueries = parts.length > 1 ? parts : [query.trim()];

      // Execute searches for each part in parallel
      const allResults: SearchResult[] = [];
      const seenIds = new Set<string>();
      const searchPromises = searchQueries.map(async (searchTerm) => {
        const { data, error } = await supabase.functions.invoke("search-documents", {
          body: { query: searchTerm, userId: user.id },
        });

        if (!error && data?.results) {
          // Use backend matchScore/matchReason when available (more reliable than title-only scoring)
          const docs = (data.results as any[]) || [];

          const normalized = docs
            .map((d) => {
              const matchScore = typeof d.matchScore === "number" ? d.matchScore : 0;
              const matchReason = typeof d.matchReason === "string" ? d.matchReason : undefined;
              return { doc: d as Document, score: matchScore, reason: matchReason, searchTerm };
            })
            .sort((a, b) => b.score - a.score);

          // How many results do we want for this term?
          // - Default: 1 doc
          // - If query includes a number ("3", "trois", etc.): take that many
          let count = 1;
          const countMatch = searchTerm.match(/\b(\d+|un|une|deux|trois|quatre|cinq|six)\b/i);
          if (countMatch) {
            const numMap: Record<string, number> = {
              un: 1,
              une: 1,
              deux: 2,
              trois: 3,
              quatre: 4,
              cinq: 5,
              six: 6,
            };
            count = numMap[countMatch[1].toLowerCase()] || parseInt(countMatch[1]) || 1;
            count = Math.min(Math.max(count, 1), 20);
          }

          return normalized.slice(0, count).map((s) => ({
            doc: s.doc,
            searchTerm: s.searchTerm,
            score: s.score,
            reason: s.reason,
          }));
        }
        return [];
      });

      const resultsPerTerm = await Promise.all(searchPromises);
      
      // Flatten and dedupe
       for (const termResults of resultsPerTerm) {
         for (const result of termResults) {
           if (!seenIds.has(result.doc.id)) {
             seenIds.add(result.doc.id);
             allResults.push({
               document: result.doc,
               found: true,
               reason: result.reason || result.searchTerm,
             });
           }
         }
       }

      setResults(allResults);
      
      // Auto-select all found documents
      const foundIds = new Set(allResults.map(r => r.document.id));
      setSelectedDocs(foundIds);

      if (allResults.length > 0) {
        setStep("select");
        setCollectionName(`Dossier: ${query.trim().substring(0, 50)}`);
      } else {
        toast.info("Aucun document trouvé pour cette recherche");
      }
    } catch (err: any) {
      console.error("Search error:", err);
      toast.error("Erreur lors de la recherche");
    } finally {
      setSearching(false);
    }
  };

  const toggleDocument = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const handleCreateCollection = async () => {
    if (selectedDocs.size === 0) {
      toast.error("Sélectionnez au moins un document");
      return;
    }

    if (!collectionName.trim()) {
      toast.error("Donnez un nom à votre collection");
      return;
    }

    setCreating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Create collection
      const { data: collection, error: collectionError } = await supabase
        .from("document_collections")
        .insert({
          user_id: user.id,
          name: collectionName.trim(),
          description: `Créé depuis la recherche: "${query}"`,
        })
        .select()
        .single();

      if (collectionError) throw collectionError;

      // Add documents to collection in the order they appear in orderedResults
      const orderedSelectedDocs = orderedResults
        .filter(r => selectedDocs.has(r.document.id))
        .map((r, index) => ({
          collection_id: collection.id,
          document_id: r.document.id,
          sort_order: index,
        }));

      const { error: docsError } = await supabase
        .from("collection_documents")
        .insert(orderedSelectedDocs);

      if (docsError) throw docsError;

      // Generate share URL
      const shareLink = `${window.location.origin}/collection/${collection.token}`;
      setShareUrl(shareLink);
      setStep("share");

      toast.success("Collection créée avec succès !");
    } catch (err: any) {
      console.error("Create collection error:", err);
      toast.error("Erreur lors de la création de la collection");
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Lien copié !");
    } catch {
      // Fallback
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("Lien copié !");
    }
  };

  const foundCount = results.filter(r => r.found).length;
  const selectedCount = selectedDocs.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-primary" />
            {step === "search" && "Collecteur de documents"}
            {step === "select" && "Sélectionner les documents"}
            {step === "share" && "Partager la collection"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Search */}
            {step === "search" && (
              <motion.div
                key="search"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <p className="text-sm text-muted-foreground">
                  Décrivez les documents dont vous avez besoin. Ajoutez une ligne par type de document.
                </p>

                <div className="space-y-3">
                  <div className="relative">
                    <Sparkles className="absolute left-3 top-3 w-4 h-4 text-primary" />
                    <Textarea
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        setSelectedExamples(new Set()); // Clear selected examples when typing manually
                      }}
                      placeholder="Ex:&#10;3 derniers bulletins de salaire&#10;Taxe foncière 2024&#10;KBIS AB Fibre"
                      className="pl-10 pr-4 min-h-[100px] resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.metaKey) {
                          handleSearch();
                        }
                      }}
                      autoFocus
                    />
                  </div>

                  <Button
                    onClick={handleSearch}
                    disabled={!query.trim() || searching}
                    className="w-full h-11 gap-2"
                  >
                    {searching ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Recherche en cours...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        Rechercher les documents
                      </>
                    )}
                  </Button>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Documents courants (cliquez pour sélectionner) :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLE_QUERIES.map((example) => (
                      <button
                        key={example}
                        onClick={() => toggleExample(example)}
                        className={cn(
                          "text-xs px-3 py-1.5 rounded-full border transition-all",
                          selectedExamples.has(example)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background hover:border-primary/50 hover:bg-primary/5"
                        )}
                      >
                        {selectedExamples.has(example) && (
                          <Check className="w-3 h-3 inline mr-1" />
                        )}
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Select */}
            {step === "select" && (
              <motion.div
                key="select"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {foundCount} document{foundCount > 1 ? "s" : ""} trouvé{foundCount > 1 ? "s" : ""}
                    <span className="text-xs ml-2 text-muted-foreground/70">
                      (glissez pour réordonner)
                    </span>
                  </p>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    {selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}
                  </Badge>
                </div>

                <Input
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  placeholder="Nom de la collection"
                  className="h-10"
                />

                <Reorder.Group
                  axis="y"
                  values={orderedResults}
                  onReorder={setOrderedResults}
                  className="space-y-2 max-h-[300px] overflow-auto"
                >
                  {orderedResults.map((result) => (
                    <DraggableDocumentItem
                      key={result.document.id}
                      result={result}
                      isSelected={selectedDocs.has(result.document.id)}
                      onToggle={() => toggleDocument(result.document.id)}
                      onPreview={() => setPreviewDoc(result.document)}
                    />
                  ))}
                </Reorder.Group>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep("search")}
                    className="flex-1"
                  >
                    Modifier la recherche
                  </Button>
                  <Button
                    onClick={handleCreateCollection}
                    disabled={selectedDocs.size === 0 || !collectionName.trim() || creating}
                    className="flex-1 gap-2"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4" />
                        Créer et partager
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Share */}
            {step === "share" && (
              <motion.div
                key="share"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6 text-center py-4"
              >
                <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>

                <div>
                  <h3 className="font-semibold text-lg">Collection créée !</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedCount} document{selectedCount > 1 ? "s" : ""} prêt{selectedCount > 1 ? "s" : ""} à être partagé{selectedCount > 1 ? "s" : ""}
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">Lien de partage :</p>
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl || ""}
                      readOnly
                      className="text-sm bg-background"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyToClipboard}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="flex-1"
                  >
                    Fermer
                  </Button>
                  <Button
                    onClick={() => window.open(shareUrl || "", "_blank")}
                    className="flex-1 gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Voir la collection
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>

      {/* Document Preview Sheet */}
      <DocumentPreviewSheet
        document={previewDoc}
        open={!!previewDoc}
        onOpenChange={(open) => !open && setPreviewDoc(null)}
      />
    </Dialog>
  );
};

export default DocumentCollector;
